import React, { isValidElement } from 'react';
import { type ComponentType } from 'react';

/**
 * If the given value is a function, return it, otherwise return a function that returns the given
 * value.
 * @param {Value | ((...args: Args) => Value) | undefined} funcOrValue - This is the value that you
 * want to normalize. It can be a function or a value.
 */
export const normalizeToFunction = <Value, Args extends any[]>(
  funcOrValue: Value | ((...args: Args) => any) | undefined
): ((...args: Args) => Value) =>
  (typeof funcOrValue === 'function' ? funcOrValue : () => funcOrValue) as any;

/**
 * Takes a function or value and optional arguments, and returns the value if it is not a function,
 * or the result of calling the function with the arguments if it is a function.
 */
export const normalizeToValue = <Value, Args extends any[]>(
  funcOrValue: ((...args: Args) => Value) | Value | undefined,
  ...args: Args
): Value extends undefined ? undefined : Value => {
  if (funcOrValue === undefined) {
    return undefined as Value extends undefined ? undefined : Value;
  }

  return (
    typeof funcOrValue === 'function'
      ? (funcOrValue as (...args: Args) => Value)(...args)
      : funcOrValue
  ) as Value extends undefined ? undefined : Value;
};

export const isReactComponent = (
  value: unknown
): value is ComponentType<{ className?: string }> => {
  return (
    typeof value === 'function' &&
    (value.prototype instanceof React.Component || isValidElement(value))
  );
};
